"use client";

import getTranslationsByKeys from "@casino/actions/getTranslationsByKeys";
import { useState } from "react";
import useDidMount from "./lifecycle/useDidMount";

declare global {
  interface Window {
    __casino_client_translations?: Map<string, string>;
  }
}

type Options = {
  enabled?: boolean;
};

const compactTranslations = (
  translations?: Partial<Record<string, string>>,
) => {
  if (!translations) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(translations).filter(
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  );
};

const useClientTranslations = (
  keys: readonly string[],
  initialTranslations?: Partial<Record<string, string>>,
  options?: Options,
) => {
  const initialClientTranslations = compactTranslations(initialTranslations);
  const [translations, setTranslations] = useState<
    Record<string, string> | undefined
  >(initialClientTranslations);
  const enabled = options?.enabled ?? true;

  useDidMount(() => {
    if (!window.__casino_client_translations) {
      window.__casino_client_translations = new Map();
    }
    const clientTranslations = window.__casino_client_translations;

    if (initialClientTranslations) {
      Object.entries(initialClientTranslations).forEach(([key, value]) => {
        clientTranslations.set(key, value);
      });
    }

    if (!enabled) {
      return;
    }

    const keysToParse = keys.filter((key: string) => {
      return !clientTranslations.has(key);
    });

    keys.forEach((key: string) => {
      if (clientTranslations.has(key)) {
        const value = clientTranslations.get(key);
        if (value === undefined) {
          return;
        }
        setTranslations((prev) => {
          return Object.assign({}, prev, {
            [key]: value,
          });
        });
      }
    });
    if (keysToParse.length > 0) {
      getTranslationsByKeys(keysToParse).then((t) => {
        if (
          typeof window !== "undefined" &&
          window.__casino_client_translations
        ) {
          Object.keys(t).forEach((key: string) => {
            window.__casino_client_translations.set(key, t[key]);
          });
        }
        setTranslations((prev) => ({ ...prev, ...t }));
      });
    }
  });

  return translations;
};

export default useClientTranslations;
